import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useModal } from "@/context/ModalContext";
import { useDirty } from "@/context/DirtyContext";
import {
  getActivities,
  createActivity,
  deleteActivity,
  updateActivity,
  getActivity,
  changeActivityState,
  getActivityExport,
  bulkActivityUpdate,
} from "@/api/program-steps";
import { capitalizeFirstWord, safePostMessageToOpener } from "@/lib/utils";
import { useAppSelector } from "@/hooks/redux";
import { addTokensToUrl } from "@/utils/auth";
import { queryClient } from "@/lib/tansack-query/queryClient";
import type { AuditSurvey } from "@/components/modals/AuditSurveysModal";

const normalizeSurveysForModal = (surveys: any[]): AuditSurvey[] =>
  (surveys ?? [])
    .map((survey: any) => ({
      id: survey?._id ?? survey?.id ?? "",
      name: survey?.name ?? survey?.surveyName ?? "",
      description: survey?.description ?? "",
      icon: survey?.icon ?? "",
      form_id: survey?.form_id ?? "",
    }))
    .filter(s => s.id && s.name);

/**
 * Interface for asset status (used in Update Organization Asset activity)
 */
export interface AssetStatus {
  category: string;
  name: string;
  description?: string;
}

/**
 * Interface for updatable asset fields (used in Update Organization Asset activity)
 */
export interface UpdatableAssetFields {
  name?: number;
  description?: number;
  other_id?: number;
  asset_form?: number;
}

export interface ReturnConditionVideoConfig {
  video_min_duration: number;
  video_max_file_size: number;
}

/**
 * Interface for the activity request body
 */
export interface CreateActivityRequestBody {
  type: string;
  name: string;
  description: string;
  phase: string[];
  icon: string;
  activity_complete_entity: string[];
  entity_type: string;
  entity: (string | undefined)[];
  store_id?: string;
  editable_after_completion_flag: number;
  // Only include if facialVerification is 1
  learning_video?: string;
  facial_bypass_flag?: number;
  facial_verification_flag?: number;
  feature_requirement?: string;
  user_to_verify?: string;
  // Rana Configuration (Share Knowledge)
  rana_recommend_flag?: number;
  // Only include if enforceLocationCheck is 1
  location_check_flag?: number;
  who_requires_location?: string;
  location_radius?: number;
  loc_tolerance_radius?: number;
  // Only include if scheduledOrUnscheduled is 1
  scheduled_activity_flag?: number;
  // Only include if scheduleRepeat is 1
  activity_repeat_flag?: number;
  activity_repetition_unit?: string;
  activity_repeat_interval?: number;
  due_date_type?: string;
  specific_due_date?: string;
  relative_activity_type?: string;
  relative_activity?: string;
  days_offset?: number;
  // Only include if activityReset is 1
  reset_flag?: number;
  reset_interval?: number;
  // Only include if activityCompletedEarly is 1 and scheduleRepeat is 1
  early_completion_flag?: number;
  min_days_from_last_event?: number;
  // Dismiss configuration (MPAFAS-110–114)
  dismiss_activity_flag?: number;
  dismiss_config?: {
    dismiss_type?: string;
    dismiss_date_type?: string;
    dismiss_specific_date?: string;
    relative_to?: string;
    relative_activity?: string;
    days_offset?: number;
    dismiss_number_of_completions?: number;
  };
  duration: number;
  priority_when_due: number;
  days_to_late: number;
  priority_when_late: number;
  days_to_very_late: number;
  priority_when_very_late: number;
  user_id: string;
  username: string;
  target_entity?: string;
  capture_biometric_flag: number;
  make_capture_compulsory_flag: number;
  recapture_existing_staff_flag: number;
  location_capture_flag: number;
  // AI Model fields
  age_model_flag?: number;
  germination_model_flag?: number;
  farm_health_model_flag?: number;
  // Perimeter Mapping fields
  perimeter_mapping_flag?: number;
  perimeter_mapping_req_type?: string;
  perimeter_mapping_badge?: string[];
  // Activity Subject Qualification fields
  activity_subject_qualification_flag?: number;
  who_requires_activity_subject_qualification?: string;
  // Badge fields
  location_badge?: string[];
  facial_verification_badge?: string[];
  activity_subject_qualification_badge?: string[];
  // Update Organization Asset fields - Asset Pre-conditions
  subcategory?: string;
  asset_ownership_scope?: string[];
  asset_owner_fv_flag?: number;
  asset_owner_fv_bypass_flag?: number;
  asset_status_precond_flag?: number;
  asset_status_precond?: AssetStatus[];
  // Update Organization Asset fields - Activity Actions
  activity_form_flag?: number;
  asset_status_update_flag?: number;
  asset_status_cond_flag?: number;
  asset_status_cond?: AssetStatus[];
  asset_assigned_owner_scope?: string[];
  asset_assigned_owner_fv_flag?: number;
  asset_assigned_owner_fv_bypass_flag?: number;
  asset_details_update_flag?: number;
  updatable_field?: UpdatableAssetFields;
  return_cond_video_flag?: number;
  return_cond_video?: ReturnConditionVideoConfig;
  // Audit Activity fields
  workflow?: string;
  workflow_level?: string[];
  supervise_entity?: string[];
  code_generate_entity?: string[];
  code_override_entity?: string[];
  audit_items?: string[];
  audit_target_entity_homepage?: string[];
  audit_store_type?: string;
  audit_target_homepage?: string;
  partial_audit_flag?: number;
  full_audit_flag?: number;
  partial_audit_qty?: string;
  full_audit_qty?: string;
  partial_variance_threshold?: number;
  full_variance_threshold?: number;
  partial_audit_items?: string[];
  full_audit_items?: string[];
  override_code_rec_flag?: number;
  override_code_audit_qty?: string;
  override_code_variance_threshold?: number;
  override_code_items?: string[];
  audit_survey_flag?: number;
  edit_inventory_diff_flag?: number;
  input_frequency?: number;
  edit_feedback_flag?: number;
  edit_feedback_configuration?: string;
  audit_feedback_flag?: number;
  large_inventory_diff_feedback?: string;
  inventory_diff_feedback_flag?: number;
  inventory_diff_feedback?: string;
  surveys?: Array<{
    name: string;
    icon: string;
    description?: string;
    enabled?: number;
  }>;
  partial_item_variance_threshold?: Array<{ item: string; variance_threshold: number; mandatory?: number }>;
  full_item_variance_threshold?: Array<{ item: string; variance_threshold: number; mandatory?: number }>;
  fv_entity?: string[];
  standalone_flag?: number;
  override_code_item_variance_threshold?: Array<{ item: string; variance_threshold: number; mandatory?: number }>;
}

/**
 * GET /activity/program/{program_id}
 * Get activities for a specific program
 */
export function useGetActivities(programId: string) {
  return useQuery({
    queryKey: ["activities", programId],
    queryFn: () => getActivities(programId),
    enabled: !!programId,
    staleTime: 0,
  });
}

/**
 * POST /activity
 * Create an activity for a program
 */
export const useCreateActivity = (programId: string, previousUrl: string) => {
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const { setIsDirty } = useDirty();
  const { organizationId } = useAppSelector(state => state.auth.user.managedOrganization ?? {});
  const encodedReturnUrl = encodeURIComponent(previousUrl);
  return useMutation({
    mutationFn: async (payload: CreateActivityRequestBody) => {
      const response = await createActivity(payload, programId);
      return { response, type: payload.type };
    },
    onMutate: () => {
      showModal("loading");
    },
    onSuccess: ({ response: { data }, type }) => {
      queryClient.invalidateQueries({
        queryKey: ["activities"],
      });
      queryClient.invalidateQueries({
        queryKey: ["activities", programId],
      });

      // Reset dirty state to allow easy navigation
      setIsDirty({ isDirt: false, confirmAction: () => {} });

      // Notify parent window to refresh its queries
      safePostMessageToOpener({
        type: "ACTIVITY_CREATED",
        programId: programId,
      });

      const isAuditActivity = type === "Audit Activity";
      // Show form redirect if form_id exists, or always for Audit Activity
      if (data.form_id || isAuditActivity) {
        const baseFormBuilderUrl = data.form_id
          ? `${process.env.NEXT_PUBLIC_FORM_BUILDER_URL}/${data.form_id}?returnUrl=${encodedReturnUrl}`
          : decodeURIComponent(encodedReturnUrl);
        const formBuilderUrl = data.form_id ? addTokensToUrl(baseFormBuilderUrl, organizationId) : baseFormBuilderUrl;

        const surveys = normalizeSurveysForModal(data.surveys ?? []);

        showModal("done", {
          title: "Activity has been created successfully",
          description: isAuditActivity
            ? "Your audit activity has been saved. You can now set up its form."
            : "You need to create an Activity form to complete the setup",
          onConfirm: () => {
            if (isAuditActivity && surveys.length > 0) {
              showModal("audit-surveys-list", {
                surveys,
                onSelectSurvey: (survey: any) => {
                  const surveyFormUrl = survey.form_id
                    ? addTokensToUrl(`${process.env.NEXT_PUBLIC_FORM_BUILDER_URL}/${survey.form_id}?returnUrl=${encodedReturnUrl}`, organizationId)
                    : formBuilderUrl;
                  window.open(surveyFormUrl, "_self");
                },
                onBack: () => window.close(),
              });
            } else {
              window.open(formBuilderUrl, "_self");
            }
          },
          onBack: () => window.close(),
          confirmText: "Continue to  Activity Form",
          backText: "Back to Activities",
          type,
        });
      } else {
        showModal("done", {
          title: "Activity has been created successfully",
          description: "Your activity has been saved.",
          onConfirm: () => window.close(),
          confirmText: "Done",
          type,
        });
      }
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description: error?.error ?? "Data unreachable at this time. Please try again later.",
      });
    },
  });
};

/**
 * PUT /activity/{activity_id}
 * Update an activity
 */
export const useUpdateActivity = (programId: string, activityId: string, previousUrl: string) => {
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const { organizationId } = useAppSelector(state => state.auth.user.managedOrganization ?? {});
  const encodedReturnUrl = encodeURIComponent(previousUrl);
  return useMutation({
    mutationFn: async (payload: CreateActivityRequestBody) => {
      const response = await updateActivity(payload, programId, activityId);
      return { response, type: payload.type };
    },
    onMutate: () => {
      showModal("loading");
    },
    onSuccess: ({ response: { data }, type }) => {
      queryClient.invalidateQueries({
        queryKey: ["activities"],
      });
      queryClient.invalidateQueries({
        queryKey: ["activities", programId],
      });
      // Notify parent window to refresh its queries
      safePostMessageToOpener({
        type: "ACTIVITY_UPDATED",
        programId: programId,
      });

      const isAuditActivity = type === "Audit Activity";
      // Show form redirect if form_id exists, or always for Audit Activity
      if (data.form_id || isAuditActivity) {
        const baseFormBuilderUrl = data.form_id
          ? `${process.env.NEXT_PUBLIC_FORM_BUILDER_URL}/${data.form_id}?returnUrl=${encodedReturnUrl}`
          : decodeURIComponent(encodedReturnUrl);
        const formBuilderUrl = data.form_id ? addTokensToUrl(baseFormBuilderUrl, organizationId) : baseFormBuilderUrl;

        const surveys = normalizeSurveysForModal(data.surveys ?? []);

        showModal("done", {
          title: "Activity has been updated successfully",
          description: isAuditActivity
            ? "Your audit activity has been saved. You can now set up its form."
            : "You need to create an Activity form to complete the setup",
          onConfirm: () => {
            if (isAuditActivity && surveys.length > 0) {
              showModal("audit-surveys-list", {
                surveys,
                onSelectSurvey: (survey: any) => {
                  const surveyFormUrl = survey.form_id
                    ? addTokensToUrl(`${process.env.NEXT_PUBLIC_FORM_BUILDER_URL}/${survey.form_id}?returnUrl=${encodedReturnUrl}`, organizationId)
                    : formBuilderUrl;
                  window.open(surveyFormUrl, "_self");
                },
                onBack: () => window.close(),
              });
            } else {
              window.open(formBuilderUrl, "_self");
            }
          },
          onBack: () => window.close(),
          confirmText: "Continue to  Activity Form",
          backText: "Back to Activities",
          type,
        });
      } else {
        showModal("done", {
          title: "Activity has been updated successfully",
          description: "Your activity has been saved.",
          onConfirm: () => window.close(),
          confirmText: "Done",
          type,
        });
      }
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description: capitalizeFirstWord(error?.error) || "Data unreachable at this time. Please try again later.",
      });
    },
  });
};

/**
 * DELETE /activity/{activity_id}
 * Delete an activity
 */
export const useDeleteActivity = () => {
  const queryClient = useQueryClient();
  const { showModal, hideModal } = useModal();
  return useMutation({
    mutationFn: ({ activityId, programId }: { activityId: string; programId: string }) => deleteActivity(activityId),
    onSuccess: (_, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["activities", programId] });

      // Notify parent window to refresh its queries
      safePostMessageToOpener({
        type: "ACTIVITY_DELETED",
        programId: programId,
      });

      showModal("done", {
        title: "Deleted",
        description: "The activity was deleted successfully.",
        onConfirm: hideModal,
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description: error.error || "There was an error deleting the activity.",
      });
    },
  });
};

/**
 * GET /activity/{activity_id}
 * Get a specific activity
 */
export const useGetActivity = (activityId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["activity", activityId],
    queryFn: () => getActivity(activityId),
    enabled: options?.enabled !== undefined ? options.enabled : !!activityId,
  });
};

/**
 * PUT /activity/{activity_id}
 * Change activity state (start/stop)
 */
export const useChangeActivityState = () => {
  const queryClient = useQueryClient();
  const { showModal, hideModal } = useModal();

  return useMutation({
    mutationFn: ({ activityId, action, user, programId }: { activityId: string; action: "start" | "stop"; user: string; programId: string }) =>
      changeActivityState(activityId, action, user),
    onSuccess: (_, { programId }) => {
      // Invalidate activity queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["activities", programId] });
      queryClient.invalidateQueries({ queryKey: ["program-timeline", programId] });

      showModal("done", {
        title: "Activity State Updated",
        description: "The activity state was updated successfully.",
        onConfirm: () => {
          hideModal();
        },
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Activity State Update Failed",
        description: error?.error ?? error?.response?.data?.error ?? "An error occurred while updating the activity state.",
      });
    },
  });
};

/**
 * Download activity export as a file
 * Following the pattern used in staff hooks like useDownloadOfficeStructure
 *
 * @param activityId - Activity ID to export
 * @param activityName - Activity name for the filename
 * @param startDate - Start date for filtering (optional, defaults to first day of current month)
 * @param endDate - End date for filtering (optional, defaults to today)
 */
export function useDownloadActivityExport() {
  const { showModal, hideModal } = useModal();
  return useMutation({
    mutationFn: async ({
      activityId,
      activityName,
      startDate,
      endDate,
    }: {
      activityId: string;
      activityName: string;
      startDate?: Date;
      endDate?: Date;
    }) => {
      // Show loading modal
      showModal("loading", {
        title: "Exporting Data",
        description: "Please wait while we prepare your export...",
      });

      // Default to current month if dates not provided
      const defaultStartDate =
        startDate ||
        (() => {
          const date = new Date();
          date.setDate(1);
          return date;
        })();
      const defaultEndDate = endDate || new Date();

      const response = await getActivityExport(activityId, defaultStartDate, defaultEndDate);
      return { response, activityName };
    },
    onSuccess: ({ response, activityName }) => {
      // Hide loading modal
      hideModal();

      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `${activityName}-${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();

      // Clean up immediately after click
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    },
    onError: (error: any) => {
      // Hide loading modal and show error
      hideModal();
      showModal("error-message", {
        title: "Export Failed",
        description: capitalizeFirstWord(error?.message) || "Unable to export the activity data. Please try again later.",
      });
    },
    // Prevent multiple simultaneous exports
    retry: false,
  });
}

export function useBulkDownloadActivityExport() {
  const { showModal, hideModal } = useModal();
  return useMutation({
    mutationFn: async (activities: Array<{ id: string; name: string }>) => {
      // Show loading modal
      showModal("loading", {
        title: "Exporting Data",
        description: `Please wait while we prepare exports for ${activities.length} ${activities.length === 1 ? "activity" : "activities"}...`,
      });

      // Call export endpoint without date filters to export everything
      const exports = await Promise.allSettled(activities.map(activity => getActivityExport(activity.id)));
      return { exports, activities };
    },
    onSuccess: ({ exports, activities }) => {
      // Hide loading modal
      hideModal();

      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      let successCount = 0;
      let failureCount = 0;

      exports.forEach((result, index) => {
        if (result.status === "fulfilled") {
          const activity = activities[index];
          const blob = new Blob([result.value.data]);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${activity.name}-${timestamp}.csv`;
          document.body.appendChild(a);
          a.click();

          // Clean up immediately after click
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);
          successCount++;
        } else {
          failureCount++;
        }
      });

      if (failureCount > 0) {
        showModal("error-message", {
          title: "Partial Export Completed",
          description: `Successfully exported ${successCount} ${successCount === 1 ? "activity" : "activities"}. Failed to export ${failureCount} ${failureCount === 1 ? "activity" : "activities"}.`,
          onConfirm: () => hideModal(),
        });
      } else if (successCount > 0) {
        showModal("done", {
          title: "Export Successful",
          description: `Successfully exported ${successCount} ${successCount === 1 ? "activity" : "activities"}.`,
          onConfirm: () => hideModal(),
        });
      }
    },
    onError: (error: any) => {
      // Hide loading modal and show error
      hideModal();
      showModal("error-message", {
        title: "Bulk Export Failed",
        description: capitalizeFirstWord(error?.message) || "Unable to export the activity data. Please try again later.",
      });
    },
    retry: false,
  });
}

export const useBulkActivityUpdate = (setSelectedRows: (newSelectedRows: Set<string>) => void) => {
  const { showModal, hideModal } = useModal();
  return useMutation({
    mutationFn: async (payload: { action: string; activity_ids: string[] }) => {
      const response = await bulkActivityUpdate(payload);
      return { response };
    },
    onSuccess: data => {
      const status = data?.response?.status;
      // Invalidate activity queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["activities"] });

      // Show modal if there is a validation error
      if (!status) {
        const errorMessage = data?.response?.data?.errors[0]?.message || "Bulk update failed due to validation errors.";
        showModal("error-message", {
          title: "Bulk Activity Update Failed",
          description: errorMessage,
          onConfirm: () => {
            hideModal();
          },
        });
      } else {
        showModal("done", {
          title: "Bulk Activity Update Successful",
          description: "The selected activities were updated successfully.",
          onConfirm: () => {
            hideModal();
            setSelectedRows(new Set());
          },
        });
      }
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Bulk Activity Update Failed",
        description: capitalizeFirstWord(error?.message) || "Unable to update the activity data. Please try again later.",
      });
    },
  });
};
